import { cn } from '@risksmart-app/atomic-ui';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import type { FC } from 'react';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ControlledDatePicker from 'src/components/form/controlled-date-picker';
import ControlledInput from 'src/components/form/controlled-input';
import ControlledRadioGroup from 'src/components/form/controlled-radio-group';
import type { Transform } from 'src/components/form/controlled-radio-group/ControlledRadioGroup';
import ControlledSelect from 'src/components/form/controlled-select';
import { ControlledSwitch } from 'src/components/form/controlled-switch/ControlledSwitch';

import type { RecurrenceEnds } from '@/utils/scheduler';
import { ENDS } from '@/utils/scheduler';
import {
  FREQUENCY_KEY,
  SCHEDULE_FREQUENCIES,
} from '@/utils/scheduler/frequencyKeys';

import type { ScheduleFormFieldData } from './scheduleFormSchema';
import { ScheduleOccurrences } from './ScheduleOccurrences';

dayjs.extend(utc);

/**
 * The write path rejects a start date before the current UTC calendar day, so
 * the picker reads today in UTC too — a local reading disagrees with it for the
 * hours either side of local midnight, offering a day the save would reject.
 */
const isUtcTodayOrLater = (date: Date): boolean =>
  dayjs(date).format('YYYY-MM-DD') >= dayjs.utc().format('YYYY-MM-DD');

const ENDS_TRANSFORM: Transform<RecurrenceEnds> = {
  input: (value) => value,
  output: (value) => (value === 'count' || value === 'until' ? value : 'never'),
};

/**
 * There is deliberately no day-of-month or day-of-week control: the engine's
 * RRULE subset is `FREQ`/`INTERVAL`/`COUNT`/`UNTIL` only. Every occurrence is
 * anchored to the start date instead, and a short month clamps onto its last day
 * rather than being skipped.
 */
export const ScheduleRecurrenceFields: FC = () => {
  const { t } = useTranslation(['common']);
  const { t: ft } = useTranslation(['common'], {
    keyPrefix: 'scheduler.frequencies',
  });
  const { t: et } = useTranslation(['common'], { keyPrefix: 'scheduler.ends' });
  const { control } = useFormContext<ScheduleFormFieldData>();
  const [repeat, ends] = useWatch({ control, name: ['Repeat', 'Ends'] });

  const frequencyOptions = useMemo(
    () =>
      Object.values(SCHEDULE_FREQUENCIES).map((frequency) => ({
        value: frequency,
        label: ft(FREQUENCY_KEY[frequency], { count: 1 }),
      })),
    [ft]
  );

  const endsItems = useMemo(
    () => ENDS.map((value) => ({ value, label: et(value) })),
    [et]
  );

  return (
    <>
      {/*
        Every calendar here portals: the accordion panel clips its content, so an
        in-flow dropdown is cut off at the panel's edge.
      */}
      <div className={cn('grid grid-cols-2 gap-4 items-start')}>
        <ControlledDatePicker
          control={control}
          name={'FirstStartDate'}
          label={t('scheduler.fields.first_start_date')}
          isDateEnabled={isUtcTodayOrLater}
          expandToViewport={true}
          testId={'schedule-start-date'}
        />

        <ControlledDatePicker
          control={control}
          name={'FirstDueDate'}
          label={t('scheduler.fields.first_due_date')}
          expandToViewport={true}
          testId={'schedule-due-date'}
        />
      </div>

      <div className={cn('pb-6')}>
        <ControlledSwitch
          control={control}
          name={'Repeat'}
          label={t('scheduler.fields.repeat')}
          testId={'schedule-repeat'}
        />
      </div>

      {repeat && (
        <>
          {/*
            A grid rather than a flex row: the input fills its column while the
            select sizes to its content, so as flex items they came out different
            widths with their controls on different lines. The interval holds a
            single number, so it takes one column to the frequency's two.
          */}
          <div className={cn('grid grid-cols-3 gap-4 items-start')}>
            {/*
              `Interval` and `Count` stay text inputs: a number input coerces the
              field to a `number`, and the schema validates the string so it can
              reject a decimal or an exponent rather than rounding one.
            */}
            <ControlledInput
              control={control}
              name={'Interval'}
              label={t('scheduler.fields.interval')}
              testId={'schedule-interval'}
            />
            <div className={cn('col-span-2')}>
              <ControlledSelect
                control={control}
                name={'Frequency'}
                label={t('scheduler.fields.frequency')}
                options={frequencyOptions}
                // Four fixed options: a filter box would add a row of chrome
                // above a list short enough to read at a glance.
                filteringType={'none'}
                testId={'schedule-frequency'}
              />
            </div>
          </div>

          <ControlledRadioGroup
            control={control}
            name={'Ends'}
            label={t('scheduler.fields.ends')}
            items={endsItems}
            transform={ENDS_TRANSFORM}
            testId={'schedule-ends'}
          />

          {ends === 'until' && (
            <ControlledDatePicker
              control={control}
              name={'Until'}
              label={t('scheduler.fields.until')}
              expandToViewport={true}
              testId={'schedule-until'}
            />
          )}

          {ends === 'count' && (
            <div className={cn('grid grid-cols-3 gap-4')}>
              <ControlledInput
                control={control}
                name={'Count'}
                label={t('scheduler.fields.count')}
                testId={'schedule-count'}
              />
            </div>
          )}
        </>
      )}

      <ScheduleOccurrences />
    </>
  );
};
